export const SMM_STAGE7_SEGMENTS=Object.freeze(['ACTIVE','HISTORY']);
export const SMM_STAGE7_SOURCE_FILTERS=Object.freeze(['ALL','ONSITE','SMM','OWN_PLATFORM','THIRD_PARTY']);
export const SMM_STAGE7_SEARCH_SCOPES=Object.freeze(['ALL','DISPLAY','PRODUCT','PHONE']);
export const SMM_STAGE7_STATUS_FILTERS=Object.freeze(['ALL','PENDING','WORKING','READY','PICKED_UP','CANCELLED','UNKNOWN']);

// Read-only presentation aliases for formal Pad facts and older LAN projections.
function statusKey(value){
  const state=String(value??'').trim().toUpperCase().replace(/\s+/g,'');
  if(['PENDING','NEW','CREATED','RECEIVED','待處理','待確認'].includes(state))return 'PENDING';
  if(['PREPARING','IN_PROGRESS','PROCESSING','進行中','製作中'].includes(state))return 'WORKING';
  if(['READY','READY_FOR_PICKUP','可取餐','準備完成/可取餐'].includes(state))return 'READY';
  if(['PICKED_UP','COLLECTED','COMPLETED','已完成','已取餐'].includes(state))return 'PICKED_UP';
  if(['CANCELLED','CANCELED','已取消'].includes(state))return 'CANCELLED';
  if(['UNKNOWN','狀態未明','狀態未讀回','部分資料','未有資料'].includes(state))return 'UNKNOWN';
  return null;
}

export function smmStage7IsHistory(row){
  const key=smmStage7StatusKey(row);
  return row?.history===true||key==='PICKED_UP'||key==='CANCELLED';
}

export function smmStage7InSegment(row,segment){
  return segment==='HISTORY'?smmStage7IsHistory(row):!smmStage7IsHistory(row);
}

export function smmStage7SourceGroup(row){
  const explicit=String(row?.sourceGroup??'').trim().toUpperCase();
  if(['ONSITE','SMM','OWN_PLATFORM','THIRD_PARTY'].includes(explicit))return explicit;
  const source=String(row?.source??'').trim().toUpperCase().replace(/[\s_-]+/g,'');
  if(!source)return 'UNKNOWN';
  if(['ONSITE','現場','COUNTER','POS','SMT','LOCAL','STORE'].includes(source))return 'ONSITE';
  if(source==='SMM'||source==='手機點單')return 'SMM';
  if(['OWNPLATFORM','自家平台','CUSTOMER','CUSTOMERAPP','APP','WEB','網站','自家APP'].includes(source))return 'OWN_PLATFORM';
  if(['THIRDPARTY','第三方','KEETA','FOODPANDA','DELIVEROO'].includes(source))return 'THIRD_PARTY';
  return 'UNKNOWN';
}

export function smmStage7MatchesSource(row,filter){
  return filter==='ALL'||smmStage7SourceGroup(row)===filter;
}

export function smmStage7Phone(row){
  if(row?.customerPhonePermitted!==true)return null;
  const value=String(row?.customerPhone??'').trim();
  return value||null;
}

export function smmStage7ItemNames(row){
  if(Array.isArray(row?.items)){
    return row.items.map(item=>String(item?.name??'').trim()).filter(Boolean);
  }
  const summary=String(row?.itemSummary??'').trim();
  return summary?[summary]:[];
}

export function smmStage7MatchesSearch(row,query,scope='ALL'){
  const q=String(query??'').trim().toLocaleLowerCase('zh-HK');
  if(!q)return true;
  const display=String(row?.displayCode??'').toLocaleLowerCase('zh-HK');
  const products=smmStage7ItemNames(row).join(' ').toLocaleLowerCase('zh-HK');
  const phone=(smmStage7Phone(row)??'').toLocaleLowerCase('zh-HK');
  if(scope==='DISPLAY')return display.includes(q);
  if(scope==='PRODUCT')return products.includes(q);
  if(scope==='PHONE')return Boolean(phone)&&phone.includes(q);
  return display.includes(q)||products.includes(q)||(Boolean(phone)&&phone.includes(q));
}

function localDateKey(value){
  const date=new Date(String(value??''));
  if(!Number.isFinite(date.getTime()))return null;
  const y=date.getFullYear();
  const m=String(date.getMonth()+1).padStart(2,'0');
  const d=String(date.getDate()).padStart(2,'0');
  return `${y}-${m}-${d}`;
}

export function smmStage7OrderTime(row){
  return String(row?.orderTime??row?.observedAt??'');
}

export function smmStage7MatchesDate(row,dateFilter,customDate,nowValue){
  if(dateFilter==='ALL')return true;
  const key=localDateKey(smmStage7OrderTime(row));
  if(!key)return false;
  if(dateFilter==='CUSTOM')return Boolean(customDate)&&key===customDate;
  const now=new Date(nowValue??Date.now());
  if(!Number.isFinite(now.getTime()))return false;
  if(dateFilter==='YESTERDAY')now.setDate(now.getDate()-1);
  const target=localDateKey(now);
  return key===target;
}

export function smmStage7ItemCount(row){
  const direct=Number(row?.itemCount);
  if(Number.isSafeInteger(direct)&&direct>=0)return direct;
  if(Array.isArray(row?.items)){
    return row.items.reduce((sum,item)=>{
      const quantity=Number(item?.quantity);
      return sum+(Number.isSafeInteger(quantity)&&quantity>0?quantity:0);
    },0);
  }
  return null;
}

export function smmStage7AmountLabel(row){
  const value=String(row?.effectiveAmountLabel??row?.amountLabel??'').trim();
  return value||'未有資料';
}

export function smmStage7StatusLabel(row){
  const key=smmStage7StatusKey(row);
  if(key==='PENDING')return '待確認';
  if(key==='WORKING')return '製作中';
  if(key==='READY')return '準備完成 / 可取餐';
  if(key==='PICKED_UP')return '已取餐';
  if(key==='CANCELLED')return '已取消';
  if(statusKey(row?.lifecycle)==='UNKNOWN')return '狀態未明';
  const supplied=String(row?.fulfillmentLabel??'').trim();
  if(supplied)return supplied;
  if(row?.readback==='UNKNOWN')return '狀態未明';
  if(row?.readback==='PARTIAL')return '部分資料';
  return row?.lifecycle?'狀態未明':'未有資料';
}

export function smmStage7StatusKey(row){
  return statusKey(row?.lifecycle)??statusKey(row?.fulfillmentLabel)??'UNKNOWN';
}

export function smmStage7MatchesStatus(row,filter){
  return filter==='ALL'||smmStage7StatusKey(row)===filter;
}

export function smmStage7Sort(rows){
  return Object.freeze([...rows].sort((a,b)=>{
    const at=Date.parse(smmStage7OrderTime(a))||0;
    const bt=Date.parse(smmStage7OrderTime(b))||0;
    if(at!==bt)return bt-at;
    return String(a?.displayCode??'').localeCompare(String(b?.displayCode??''));
  }));
}

export function smmStage7ConnectionState(connection,hasRows){
  if(connection==='LOADING')return Object.freeze({kind:'LOADING',title:'正在讀取訂單',detail:'只會顯示正式訂單投影。'});
  if(connection==='NOT_CONNECTED')return Object.freeze({kind:'OFFLINE',title:'訂單服務離線',detail:hasRows?'保留最近一次只讀資料；不會建立新訂單狀態。':'目前未有可讀取嘅正式訂單資料。'});
  if(connection==='STALE')return Object.freeze({kind:'STALE',title:'訂單資料較舊',detail:'保留最近一次正式投影；可重新整理。'});
  if(connection==='PARTIAL')return Object.freeze({kind:'PARTIAL',title:'部分訂單資料已讀取',detail:'已成功部分會保留，唔會當成總失敗。'});
  if(connection==='UNKNOWN')return Object.freeze({kind:'UNKNOWN',title:'訂單資料狀態未明',detail:'只做重新讀取，不會推斷或改寫訂單狀態。'});
  if(connection==='ERROR')return Object.freeze({kind:'ERROR',title:'訂單資料讀取失敗',detail:'可重新整理；不會執行任何訂單 mutation。'});
  return null;
}
